import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { Globe, Crosshair, Lightning, Envelope, SpinnerGap, CheckCircle, MapPin } from "@phosphor-icons/react";
import Sidebar from "@/components/Sidebar";
import ProspectForm from "@/components/ProspectForm";
import ProspectList from "@/components/ProspectList";
import ProspectDetail from "@/components/ProspectDetail";
import StatsBar from "@/components/StatsBar";
import BatchProgress from "@/components/BatchProgress";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Dashboard() {
  const [prospects, setProspects] = useState([]);
  const [selectedProspect, setSelectedProspect] = useState(null);
  const [prospectDetail, setProspectDetail] = useState(null);
  const [stats, setStats] = useState({ total_prospects: 0, audited: 0, emails_generated: 0, avg_score: 0 });
  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [discovering, setDiscovering] = useState(false);
  const [discoverResult, setDiscoverResult] = useState(null);
  const [filterZip, setFilterZip] = useState("");
  const [filterCity, setFilterCity] = useState("");
  const [activeFilter, setActiveFilter] = useState(null);

  // Campaign state (lives at Dashboard level, shared via sidebar)
  const [activeCampaign, setActiveCampaign] = useState(null);

  // Batch state
  const [batchRunning, setBatchRunning] = useState(null); // "audit" | "email" | "both" | null
  const [batchProgress, setBatchProgress] = useState(null);

  const fetchProspects = useCallback(async () => {
    try {
      const params = {};
      if (filterZip) params.zip_code = filterZip;
      if (filterCity) params.city = filterCity;
      const res = await axios.get(`${API}/prospects`, { params });
      setProspects(res.data);
    } catch (e) {
      console.error("Failed to fetch prospects", e);
    }
  }, [filterZip, filterCity]);

  const fetchStats = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/dashboard/stats`);
      setStats(res.data);
    } catch (e) {
      console.error("Failed to fetch stats", e);
    }
  }, []);

  const fetchProspectDetail = useCallback(async (id) => {
    try {
      const res = await axios.get(`${API}/prospects/${id}`);
      setProspectDetail(res.data);
    } catch (e) {
      console.error("Failed to fetch prospect detail", e);
    }
  }, []);

  useEffect(() => {
    fetchProspects();
    fetchStats();
  }, [fetchProspects, fetchStats]);

  const handleSelectProspect = (prospect) => {
    setSelectedProspect(prospect);
    fetchProspectDetail(prospect.id);
  };

  const handleAddProspect = async (data) => {
    try {
      await axios.post(`${API}/prospects`, data);
      setShowForm(false);
      fetchProspects();
      fetchStats();
    } catch (e) {
      console.error("Failed to add prospect", e);
    }
  };

  const handleDeleteProspect = async (id, blockDomain = false) => {
    try {
      await axios.delete(`${API}/prospects/${id}`, { params: { block_domain: blockDomain } });
      if (selectedProspect?.id === id) {
        setSelectedProspect(null);
        setProspectDetail(null);
      }
      fetchProspects();
      fetchStats();
    } catch (e) {
      console.error("Failed to delete prospect", e);
    }
  };

  const handleRunAudit = async (id) => {
    setLoading(true);
    try {
      await axios.post(`${API}/prospects/${id}/audit`);
      fetchProspects();
      fetchStats();
      fetchProspectDetail(id);
    } catch (e) {
      console.error("Audit failed", e);
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateEmail = async (id, options = {}) => {
    setLoading(true);
    try {
      await axios.post(`${API}/prospects/${id}/generate-email`, {
        tone: options.tone || "professional",
        sender_name: options.sender_name || "ChiroTracker",
        sender_company: options.sender_company || null,
        campaign_id: options.campaign_id || null,
      });
      fetchProspectDetail(id);
      fetchStats();
    } catch (e) {
      console.error("Email generation failed", e);
    } finally {
      setLoading(false);
    }
  };

  const handleDiscover = async ({ zip_code, city, state, business_type }) => {
    setDiscovering(true);
    setDiscoverResult(null);
    try {
      const res = await axios.post(`${API}/prospects/discover`, {
        zip_code: zip_code || null,
        city: city || null,
        state: state || null,
        business_type: business_type || "chiropractor",
        max_results: 15,
      });
      setDiscoverResult(res.data);

      // Auto-filter to show only results from this search
      if (city) {
        setFilterCity(city);
        setFilterZip("");
        setActiveFilter({ label: city + (state ? `, ${state}` : "") });
      } else if (zip_code) {
        setFilterZip(zip_code);
        setFilterCity("");
        setActiveFilter({ label: `ZIP ${zip_code}` });
      }

      fetchStats();
    } catch (e) {
      console.error("Discovery failed", e);
    } finally {
      setDiscovering(false);
    }
  };

  const clearFilter = () => {
    setFilterZip("");
    setFilterCity("");
    setActiveFilter(null);
  };

  const handleClearList = async () => {
    try {
      await axios.delete(`${API}/prospects/clear-list`);
      setProspects([]);
      setSelectedProspect(null);
      setProspectDetail(null);
      setActiveFilter(null);
      fetchStats();
    } catch (e) {
      console.error("Clear failed", e);
    }
  };

  // ── Batch Operations ──────────────────────────────
  const handleBatchAudit = async () => {
    setBatchRunning("audit");
    setBatchProgress({ phase: "audit", status: "running", message: "Auditing all prospects..." });
    try {
      const res = await axios.post(`${API}/batch/audit`, {});
      setBatchProgress({
        phase: "audit",
        status: "done",
        message: `Audited ${res.data.completed}/${res.data.total} prospects`,
        results: res.data.results,
      });
      fetchProspects();
      fetchStats();
    } catch (e) {
      console.error("Batch audit failed", e);
      setBatchProgress({ phase: "audit", status: "error", message: "Batch audit failed" });
    } finally {
      setBatchRunning(null);
    }
  };

  const handleBatchEmails = async () => {
    setBatchRunning("email");
    setBatchProgress({ phase: "email", status: "running", message: "Generating emails for all audited prospects..." });
    try {
      const res = await axios.post(`${API}/batch/generate-emails`, {
        tone: "professional",
        sender_name: "ChiroTracker",
        campaign_id: activeCampaign?.id || null,
      });
      setBatchProgress({
        phase: "email",
        status: "done",
        message: `Generated ${res.data.completed}/${res.data.total} emails`,
        results: res.data.results,
      });
      fetchProspects();
      fetchStats();
    } catch (e) {
      console.error("Batch email failed", e);
      setBatchProgress({ phase: "email", status: "error", message: "Batch email generation failed" });
    } finally {
      setBatchRunning(null);
    }
  };

  const handleBatchAll = async () => {
    setBatchRunning("both");

    // Phase 1: Audit
    setBatchProgress({ phase: "audit", status: "running", message: "Step 1/2: Auditing all prospects..." });
    try {
      const auditRes = await axios.post(`${API}/batch/audit`, {});
      setBatchProgress({
        phase: "audit",
        status: "done",
        message: `Step 1/2: Audited ${auditRes.data.completed}/${auditRes.data.total} prospects`,
        results: auditRes.data.results,
      });
      fetchProspects();
      fetchStats();
    } catch (e) {
      console.error("Batch audit failed", e);
      setBatchProgress({ phase: "audit", status: "error", message: "Batch audit failed" });
      setBatchRunning(null);
      return;
    }

    // Phase 2: Generate emails
    setBatchProgress({ phase: "email", status: "running", message: "Step 2/2: Generating cold emails..." });
    try {
      const emailRes = await axios.post(`${API}/batch/generate-emails`, {
        tone: "professional",
        sender_name: "ChiroTracker",
        campaign_id: activeCampaign?.id || null,
      });
      setBatchProgress({
        phase: "complete",
        status: "done",
        message: `Done! Audited prospects and generated ${emailRes.data.completed} personalized emails`,
        results: emailRes.data.results,
      });
      fetchProspects();
      fetchStats();
    } catch (e) {
      console.error("Batch email failed", e);
      setBatchProgress({ phase: "email", status: "error", message: "Email generation failed" });
    } finally {
      setBatchRunning(null);
    }
  };

  const pendingAudits = prospects.filter(p => p.audit_status !== "completed" && p.website).length;
  const auditedNoEmail = prospects.filter(p => p.audit_status === "completed" && p.email_status !== "generated").length;

  return (
    <div className="flex h-screen overflow-hidden" data-testid="dashboard">
      <Sidebar
        filterZip={filterZip}
        setFilterZip={setFilterZip}
        onSearch={fetchProspects}
        onDiscover={handleDiscover}
        onAddNew={() => setShowForm(true)}
        stats={stats}
        discovering={discovering}
        activeCampaign={activeCampaign}
        onSelectCampaign={setActiveCampaign}
      />

      <div className="flex-1 flex flex-col overflow-hidden">
        <StatsBar stats={stats} />

        {/* Discover result banner */}
        <AnimatePresence>
          {discoverResult && (
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className={`${discoverResult.discovered > 0 ? "bg-[--primary-light] border-[--primary]" : "bg-[--warning-bg] border-[--warning]"} border-b px-6 py-3 flex items-center justify-between`}
              data-testid="discover-result-banner"
            >
              <div className="flex items-center gap-2">
                <Crosshair size={16} className={discoverResult.discovered > 0 ? "text-[--primary]" : "text-[#B38F00]"} weight="bold" />
                <span className={`text-sm font-medium ${discoverResult.discovered > 0 ? "text-[--primary]" : "text-[#B38F00]"}`}>
                  {discoverResult.discovered > 0
                    ? `Found ${discoverResult.discovered} practices — ${discoverResult.saved} added to your list`
                    : "No results found. Try adding a state (e.g. FL) or try again in a moment."
                  }
                </span>
              </div>
              <button
                onClick={() => setDiscoverResult(null)}
                className="text-xs text-[--primary] hover:underline font-medium"
                data-testid="dismiss-discover-result"
              >
                Dismiss
              </button>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Batch progress banner */}
        <AnimatePresence>
          {batchProgress && (
            <BatchProgress
              progress={batchProgress}
              onDismiss={() => setBatchProgress(null)}
            />
          )}
        </AnimatePresence>

        <div className="flex-1 flex overflow-hidden">
          {/* Prospect List Panel */}
          <div className="w-full md:w-[380px] border-r border-[--border] flex flex-col overflow-hidden bg-white">
            <div className="p-4 border-b border-[--border]">
              <div className="flex items-center justify-between mb-3">
                <h2 className="text-lg font-semibold tracking-tight" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
                  Prospects
                  <span className="ml-2 text-sm font-normal text-[--text-muted]">({prospects.length})</span>
                </h2>
                <div className="flex items-center gap-2">
                  {prospects.length > 0 && (
                    <button
                      data-testid="clear-list-btn"
                      onClick={handleClearList}
                      className="text-[10px] text-[--text-muted] hover:text-[--danger] transition-colors"
                    >
                      Clear list
                    </button>
                  )}
                  <button
                    data-testid="add-prospect-btn"
                    onClick={() => setShowForm(true)}
                    className="flex items-center gap-1.5 bg-[--text-primary] text-white px-3 py-1.5 text-xs font-medium hover:bg-[--text-secondary] transition-all active:scale-95"
                  >
                    + Manual
                  </button>
                </div>
              </div>

              {/* Active filter chip */}
              {activeFilter && (
                <div className="flex items-center gap-2 mb-3" data-testid="active-filter">
                  <span className="flex items-center gap-1.5 px-3 py-1.5 bg-[--primary-light] text-[--primary] text-xs font-medium">
                    <MapPin size={12} weight="bold" />
                    {activeFilter.label}
                    <button
                      data-testid="clear-filter-btn"
                      onClick={clearFilter}
                      className="ml-1 hover:bg-[--primary] hover:text-white rounded-full w-4 h-4 flex items-center justify-center text-[10px] transition-colors"
                    >
                      x
                    </button>
                  </span>
                  <button
                    onClick={clearFilter}
                    className="text-[10px] text-[--text-muted] hover:text-[--text-primary] underline"
                  >
                    Show all
                  </button>
                </div>
              )}

              {/* Batch action buttons */}
              {prospects.length > 0 && (
                <div className="flex items-center gap-2">
                  <button
                    data-testid="batch-audit-all-btn"
                    onClick={handleBatchAll}
                    disabled={!!batchRunning || pendingAudits === 0}
                    className="flex-1 flex items-center justify-center gap-1.5 bg-[--primary] text-white px-3 py-2 text-xs font-medium hover:bg-[--primary-hover] transition-all active:scale-95 disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    {batchRunning === "both" ? (
                      <><SpinnerGap size={14} className="animate-spin" /> Processing...</>
                    ) : (
                      <><Lightning size={14} weight="bold" /> Audit All + Generate Emails</>
                    )}
                  </button>
                  {pendingAudits > 0 && !batchRunning && (
                    <button
                      data-testid="batch-audit-btn"
                      onClick={handleBatchAudit}
                      disabled={!!batchRunning}
                      className="flex items-center gap-1 border border-[--primary] text-[--primary] px-3 py-2 text-xs font-medium hover:bg-[--primary-light] transition-all active:scale-95 disabled:opacity-40"
                      title={`Audit ${pendingAudits} pending prospects`}
                    >
                      {batchRunning === "audit" ? (
                        <SpinnerGap size={14} className="animate-spin" />
                      ) : (
                        <><Lightning size={14} /> Audit ({pendingAudits})</>
                      )}
                    </button>
                  )}
                  {auditedNoEmail > 0 && !batchRunning && (
                    <button
                      data-testid="batch-email-btn"
                      onClick={handleBatchEmails}
                      disabled={!!batchRunning}
                      className="flex items-center gap-1 border border-[--primary] text-[--primary] px-3 py-2 text-xs font-medium hover:bg-[--primary-light] transition-all active:scale-95 disabled:opacity-40"
                      title={`Generate emails for ${auditedNoEmail} audited prospects`}
                    >
                      {batchRunning === "email" ? (
                        <SpinnerGap size={14} className="animate-spin" />
                      ) : (
                        <><Envelope size={14} /> Emails ({auditedNoEmail})</>
                      )}
                    </button>
                  )}
                </div>
              )}
            </div>
            <div className="flex-1 overflow-y-auto">
              <ProspectList
                prospects={prospects}
                selected={selectedProspect}
                onSelect={handleSelectProspect}
                onDelete={handleDeleteProspect}
              />
            </div>
          </div>

          {/* Detail Panel */}
          <div className="hidden md:flex flex-1 flex-col overflow-hidden bg-[--bg-muted]">
            <AnimatePresence mode="wait">
              {selectedProspect && prospectDetail ? (
                <motion.div
                  key={selectedProspect.id}
                  initial={{ opacity: 0, x: 16 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -16 }}
                  className="flex-1 overflow-y-auto"
                >
                  <ProspectDetail
                    prospect={prospectDetail.prospect}
                    audit={prospectDetail.audit}
                    emails={prospectDetail.emails}
                    loading={loading}
                    onRunAudit={() => handleRunAudit(selectedProspect.id)}
                    onGenerateEmail={(opts) => handleGenerateEmail(selectedProspect.id, opts)}
                    activeCampaign={activeCampaign}
                  />
                </motion.div>
              ) : (
                <motion.div
                  key="empty"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="flex-1 flex items-center justify-center"
                >
                  <div className="text-center max-w-sm" data-testid="empty-state">
                    <Crosshair size={48} className="mx-auto mb-4 text-[--primary]" weight="duotone" />
                    <h3 className="text-xl font-bold mb-2" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
                      Pick a niche and location
                    </h3>
                    <p className="text-[--text-muted] text-sm">
                      Use the sidebar to discover local businesses in any area. Then hit <strong>"Audit All + Generate Emails"</strong> to process everything at once.
                    </p>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </div>

      <AnimatePresence>
        {showForm && (
          <ProspectForm
            onSubmit={handleAddProspect}
            onClose={() => setShowForm(false)}
          />
        )}
      </AnimatePresence>
    </div>
  );
}
